// utils > Dates
import { numDaysInMonth } from './Constants'

type IntlDateTimeFormatOptions = {
	localeMatcher: 'lookup' | 'best fit'
	timeZone: string
	hour12: boolean
	hourCycle: 'h11' | 'h12' | 'h23' | 'h24'
	formatMatcher: 'basic' | 'best fit'
	weekday: 'long' | 'short' | 'narrow'
	era: 'long' | 'short' | 'narrow'
	year: 'numeric' | '2-digit'
	month: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow'
	day: 'numeric' | '2-digit'
	hour: 'numeric' | '2-digit'
	minute: 'numeric' | '2-digit'
	second: 'numeric' | '2-digit'
	timeZoneName: 'long' | 'short'
}

type WeekFuncArgProps = {
	weekStartsOn: number
	modifyInPlace: boolean
}

type LocaleArgs = {
	locale: string
	localeFormat: Partial<IntlDateTimeFormatOptions>
}

export function columnNamesForThisISOWeek(optionalArgs: Partial<WeekFuncArgProps> & Partial<LocaleArgs> = {}) {
	const finalOptionalArgs = Object.assign({}, optionalArgs, { weekStartsOn: 1 })

	return columnNamesForThisWeek(finalOptionalArgs)
}

export function columnNamesForThisWeek(optionalArgs: Partial<WeekFuncArgProps> & Partial<LocaleArgs> = {}) {
	const { locale, localeFormat } = optionalArgs

	const formatOptions = localeFormat || { day: 'numeric' }
	const intlFormatter = new Intl.DateTimeFormat(locale || 'en-US', formatOptions)
	const dates = rangeThisWeek(optionalArgs)

	const result = dates.map(d => intlFormatter.format(d))

	return result
}

export function format(date: Date, locale: string, localeFormat?: Partial<IntlDateTimeFormatOptions>) {
	const intlFormatter = new Intl.DateTimeFormat(locale, localeFormat)

	return intlFormatter.format(date)
}

export function rangeThisISOWeek() {
	return rangeThisWeek({ weekStartsOn: 1 })
}

export function rangeThisWeek(optionalArgs: Partial<WeekFuncArgProps> = { modifyInPlace: false }) {
	const current = new Date()

	return rangeForDateWeek(current, optionalArgs)
}

/**
 * calculates the range of dates based on the provided date and weekStartsOn 0-6 index value
 * @returns an array of Date instances
 */
export function rangeForDateWeek(d: Date, optionalArgs: Partial<WeekFuncArgProps> = { modifyInPlace: false }) {
	// force no in-place modification
	optionalArgs.modifyInPlace = false

	const start = startOfWeek(d, optionalArgs)
	const end = endOfWeek(d, optionalArgs)

	return dateRange(start, end)
}

/**
 * @returns an array of dates from the start to the end
 */
export function dateRange(start: Date, end: Date) {
	const result: Date[] = []
	const diff = differenceInDays(start, end)

	for (let i = 0; i < diff; i++) {
		const d = new Date(start.getTime())
		d.setDate(d.getDate() + i)

		result.push(d)
	}

	return result
}

/**
 * get a new Date with the provided milliseconds added
 * @param date 
 * @param milliseconds 
 * @returns new Date instance with the milliseconds added
 */
export function addMilliseconds(date: Date, milliseconds: number) {
	return new Date(date.getTime() + milliseconds)
}

/**
 * @returns an integer value inclusive of any partial days (e.g. if exact difference is 1.5, returns 2)
 */
export function differenceInDays(d1: Date, d2: Date, useAbsolute: boolean = false) {
	let result = 0
	const d1Ts = d1.getTime()
	const d2Ts = d2.getTime()
	const secondTzGreaterOffset = d2.getTimezoneOffset() > d1.getTimezoneOffset()

	let days = (d2Ts - d1Ts) / (1000 * 60 * 60 * 24)
	if (secondTzGreaterOffset) days-- // hacky way to adjust for a daylight savings difference

	result = days < 0 ? Math.floor(days) : Math.ceil(days)

	return useAbsolute ? Math.abs(result) : result
}

export function endOfWeek(d: Date, optionalArgs: Partial<WeekFuncArgProps> = { modifyInPlace: false }): Date {
	const { weekStartsOn } = optionalArgs
	const shiftedDate = calcEndDateOfWeek(d, weekStartsOn)
	const newDate = optionalArgs.modifyInPlace ? d : new Date(d.getTime())

	newDate.setDate(shiftedDate)
	endOfDay(newDate)

	return newDate
}

/**
 * sets the date instance value to the start of the week
 * @returns the same Date instance (mutates the passed Date instance)
 */
export function startOfWeek(d: Date, optionalArgs: Partial<WeekFuncArgProps> = { modifyInPlace: false }): Date {
	const { weekStartsOn } = optionalArgs
	const shiftedDate = calcStartDateOfWeek(d, weekStartsOn)
	const newDate = optionalArgs.modifyInPlace ? d : new Date(d.getTime())

	newDate.setDate(shiftedDate)
	startOfDay(newDate)

	return newDate
}

/**
 * partial application of calcStartOrEndDateOfWeek, sets to subtract
 */
function calcEndDateOfWeek(d: Date, weekStartsOn?: number) {
	return calcStartOrEndDateOfWeek(d, weekStartsOn)
}

/**
 * partial application of calcStartOrEndDateOfWeek, leaves default of addition
 */
function calcStartDateOfWeek(d: Date, weekStartsOn?: number) {
	return calcStartOrEndDateOfWeek(d, weekStartsOn, true)
}

/**
 * skews the difference calculation based on the provided weekStartsOn 0 - 6 index value
 */
function calcStartOrEndDateOfWeek(d: Date, weekStartsOn: number = 0, isStart: boolean = false) {
	const weekDay = d.getDay()
	let difference = weekStartsOn - weekDay

	if (!isStart && difference <= 0) difference += 6

	return d.getDate() + difference
}

/**
 * sets the date instance to the start of its day
 * @returns the same Date instance (mutates the passed Date instance)
 */
export function startOfDay(d: Date): Date {
	d.setHours(0)
	d.setMinutes(0)
	d.setSeconds(0)
	d.setMilliseconds(0)

	// return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
	return d
}

/**
 * sets the date instance to the end of its day
 * @returns the same Date instance (mutates the passed Date instance)
 */
export function endOfDay(d: Date): Date {
	d.setHours(23)
	d.setMinutes(59)
	d.setSeconds(59)
	d.setMilliseconds(999)

	return d
}

/**
 * determines the number of days in the month of the provided date instance
 * @param date
 */
export function daysInMonth(date: Date): number {
	const month = date.getMonth()

	if (month === 1 && isLeapYear(date)) return 29

	return numDaysInMonth[month]
}

/**
 * 1st date of the month for the month of the provided date
 * @param date 
 * @param modifyInPlace - default false
 * @returns new Date instance or existing if modifyInPlace
 */
export function startOfMonth(date: Date, modifyInPlace: boolean = false): Date {
	const newDate = modifyInPlace ? date : new Date(date.getTime())
	newDate.setDate(1)
	startOfDay(newDate)

	return newDate
}

export function isFirstOfMonth(date: Date): boolean {
	return date.getDate() === 1
}

export function isLastDateOfMonth(date: Date): boolean {
	const numDays = daysInMonth(date)

	return date.getDate() === numDays
}

/**
 * calculates the startOf the previous calendar month
 * @param date 
 * @param modifyInPlace 
 */
export function previousMonth(date: Date, modifyInPlace?: boolean): Date {
	const newMonth = addMonths(date, -1, modifyInPlace)
	return startOfMonth(newMonth, modifyInPlace)
}

/**
 * calculates the startOf the next calendar month
 * @param date 
 * @param modifyInPlace 
 */
export function nextMonth(date: Date, modifyInPlace?: boolean): Date {
	const newMonth = addMonths(date, 1, modifyInPlace)

	return startOfMonth(newMonth, modifyInPlace)
}

/**
 * adds or subtracts (if negative) the designated number of months from the date
 * @param date 
 * @param numMonths 
 * @param modifyInPlace 
 */
export function addMonths(date: Date, numMonths: number, modifyInPlace: boolean = false): Date {
	const newDate = modifyInPlace ? date : new Date(date.getTime())
	addMonthsInPlace(newDate, numMonths)

	return newDate
}

/**
 * last date of the month for the month of the provided date
 * @param date 
 * @param modifyInPlace - default false
 * @returns new Date instance or existing if modifyInPlace
 */
export function endOfMonth(date: Date, modifyInPlace: boolean = false): Date {
	const newDate = modifyInPlace ? date : new Date(date.getTime())

	// set the date to the end of the month
	const endOfMonth = daysInMonth(newDate)
	newDate.setDate(endOfMonth)

	// set the time to the end of the day
	endOfDay(newDate)

	return newDate
}

/**
 * gets an array of all the dates for the month of the provided date
 * @param date 
 */
export function dateRangeForMonth(date: Date): Date[] {
	const start = startOfMonth(date)
	const end = endOfMonth(date)

	return dateRange(start, end)
}

/**
 * determines whether the year of the provided date meets the criteria of a leap year
 * @param date - javascript Date object
 */
export function isLeapYear(date: Date): boolean {
	const year = date.getFullYear()

	// leap year is any year divisible by 4, except centennial years not divisible by 400
	if (year % 100 === 0 && year % 400 > 0) return false

	return year % 4 === 0
}

export function isEqual(d1: Date, d2: Date): boolean {
	return d1.getTime() === d2.getTime()
}

export function isBefore(d1: Date, d2: Date): boolean {
	return differenceInDays(d1, d2) < 0
}

export function isAfter(d1: Date, d2: Date): boolean {
	return differenceInDays(d1, d2) > 0
}

export function isSameYear(d1: Date, d2: Date): boolean {
	return d1.getFullYear() === d2.getFullYear()
}

export function isSameMonth(d1: Date, d2: Date): boolean {
	return isSameYear(d1, d2) && d1.getMonth() === d2.getMonth()
}

export function isSameDate(d1: Date, d2: Date): boolean {
	return isSameMonth(d1, d2) && d1.getDate() === d2.getDate()
}

export function isToday(date: Date): boolean {
	return isSameDate(date, new Date())
}

function addMonthsInPlace(date: Date, numMonths: number) {
	const newMonth = date.getMonth() + numMonths
	date.setMonth(newMonth)
}
